"use client";

import { Button } from "@/components/atoms/button";
import { Link } from "@/components/atoms/link/Link";
import { RichtextRenderer } from "@/components/atoms/rich-text-render";import { JapanArea } from "@/types/generated";
import { ChevronRightIcon } from "lucide-react";
import { useMemo, useState } from "react";

export interface IDestinationHotelItem {
  documentId: string;
  hide: string;
  show_more: string;
  title: string;
}
interface DestinationHotelGroupProps {
  destinationJapanAreas: JapanArea[];
  regionName?: string;
  destinationHotelItem?: IDestinationHotelItem;
}

const DESKTOP_COLLAPSED_MAX_HEIGHT = "600px";
const initialDesktopItemCount = 12;

/**
 * DestinationHotelGroup Component
 * Displays grouped hotel lists for different Japan regions or areas.
 * - Supports two modes:
 *   1. Main destination view: shows all regions with "Show more" option.
 *   2. Region detail view: lists hotels within a specific region.
 * - Handles sorting of areas and hotels based on defined order fields.
 */
export const DestinationHotelGroup = ({
  destinationJapanAreas,
  regionName,
  destinationHotelItem,
}: DestinationHotelGroupProps) => {
  const [showAllDesktop, setShowAllDesktop] = useState(false);

  const areas = useMemo(() => {
    const sortData = (destinationJapanAreas || []).sort(
      (a, b) =>
        Number.parseInt(a?.state_code as string) -
        Number.parseInt(b?.state_code as string),
    );

    // Map hotel document IDs to their display order
    for (const area of sortData) {
      const orderMap = new Map<string, number>();

      // Build order map
      for (const item of area?.selected_hotels || []) {
        orderMap.set(item.documentId, item.order);
      }

      // 🟢 Extracted assignment (no inline ||= inside sort)
      if (!area.hotels) {
        area.hotels = [];
      }

      // Sort hotels
      area.hotels.sort((a, b) => {
        const orderA = orderMap.get(a?.documentId ?? "") ?? 0;
        const orderB = orderMap.get(b?.documentId ?? "") ?? 0;
        return orderA - orderB;
      });
    }
    return sortData;
  }, [destinationJapanAreas]);

  const showDesktopShowMore = areas?.length > initialDesktopItemCount;
  const { title, show_more, hide } = destinationHotelItem || {};

  return (
    <div className="flex flex-col gap-8 pb-0 max-md:px-4 md:pb-20">
      <div className="relative">
        <h2
          className={`max-md:mt-5 max-md:text-2xl max-md:leading-9 ${regionName ? "" : "mb-5"} mt-5 text-center text-4xl leading-11 font-[var(--font-semibold)] font-semibold tracking-[-0.02em] text-[var(--text-base-primary)] md:text-[24px]`}
        >
          {title}
        </h2>
        {regionName ? (
          <div className="py-8">
            {areas.map((area, index) => (
              <div
                key={`desktop-area-detail-${area?.documentId || index}`}
                className="mb-10"
              >
                <h2 className="mb-3 text-center text-3xl font-[var(--font-semibold)] text-[var(--text-base-primary)] md:text-[24px]">
                  {area?.area_name || regionName || "Unknown Region"}
                </h2>
                <hr className="mb-5 border-t border-[var(--stroke-gray-light)]" />
                <div className="flex flex-wrap gap-x-8 gap-y-3 md:justify-center">
                  {(area?.hotels || []).map((hotel) => (
                    <div
                      key={hotel?.documentId}
                      className="mb-2 text-sm max-md:w-full"
                    >
                      <Link
                        target="_blank"
                        href={hotel?.slug || ""}
                        className="text-sm text-[var(--text-blue-gray-700)]"
                      >
                        {hotel?.properties?.hotel_name}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div
              style={{
                maxHeight:
                  !showAllDesktop && showDesktopShowMore
                    ? DESKTOP_COLLAPSED_MAX_HEIGHT
                    : "none",
                overflow:
                  !showAllDesktop && showDesktopShowMore ? "hidden" : "visible",
                transition: "max-height 0.3s ease-in-out",
              }}
            >
              <div
                className="sm:columns-2 md:columns-3 lg:columns-4"
                style={{
                  columnGap: "1.5rem",
                }}
              >
                {areas.map((area, index) => (
                  <div
                    key={`desktop-area-main-${area?.documentId || index}`}
                    className="mb-6 flex flex-col rounded-lg border border-[var(--stroke-gray-light)] bg-white shadow-lg"
                    style={{ breakInside: "avoid-column" }}
                  >
                    <Link href={`${area?.slug_attraction}`}>
                      <div className="flex h-11 items-center justify-between border-b border-[var(--stroke-gray-light)] px-3 pt-3 pb-2 !text-base leading-6 text-[var(--text-base-primary)]">
                        <RichtextRenderer
                          content={area?.area_name || ""}
                          customStyle={{
                            "": {
                              fontWeight: "var(--font-semibold)",
                              fontSize: "inherit",
                            },
                          }}
                        />
                        <ChevronRightIcon size={20} className="text-gray-500" />
                      </div>
                    </Link>
                    <div className="flex flex-col gap-3 space-y-2 p-4">
                      {(area?.hotels || []).map((hotel) => (
                        <Link
                          key={hotel?.documentId}
                          href={`${hotel?.slug}`}
                          className="m-0"
                        >
                          <div
                            key={hotel?.documentId}
                            className="text-xs leading-4 text-[var(--text-gray-normal)]"
                          >
                            <RichtextRenderer
                              customStyle={{
                                "": {
                                  fontWeight: "var(--font-medium)",
                                  fontSize: "inherit",
                                  color: "inherit",
                                },
                              }}
                              content={hotel?.properties?.hotel_name || ""}
                            />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {showDesktopShowMore && (
              <>
                <div
                  className={`${showAllDesktop ? "hidden" : "block"} pointer-events-none absolute right-0 bottom-0 left-0 z-2 h-full w-full`}
                  style={{
                    background:
                      "linear-gradient(to top, #fff 0%, rgba(255,255,255,0.1) 80%, rgba(255,255,255,0.1) 90%, rgba(255,255,255,0) 100%)",
                  }}
                />
                <div
                  className={`${showAllDesktop ? "mt-10" : "absolute right-0 bottom-10 left-0 z-2"} flex justify-center`}
                >
                  <Button
                    onClick={() => setShowAllDesktop(!showAllDesktop)}
                    variant={"primary"}
                    hierarchy={"outline"}
                    className="pointer-events-auto mb-4 h-12 text-base leading-6 font-[var(--font-medium)] shadow"
                  >
                    {showAllDesktop ? hide : show_more}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
